import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './dealer-dashboard.css';
import {
  BottomNav,
  CoinIcon,
  ColorRail,
  SELLER_NAV,
  ScaleIcon,
  ShieldTrustIcon,
  SuvIcon,
  TopBar,
} from '../../../commodity/components/caria';
import { fetchMyListings, SellerListing } from '../api/seller.api';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

/**
 * DealerDashboard (W-S-DASH-02 fidelity) — dealer cockpit. Inventory KPIs +
 * "Top inventario" are LIVE (GET /vehicles/mine). The Lead Inbox is a clearly
 * labeled preview (a leads/CRM pipeline is a T1 capability, not yet built).
 */
const FALLBACK = [
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=300&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=300&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=300&h=200&fit=crop&q=80',
];
const usd = (n?: number) => (n != null ? `$${Math.round(n).toLocaleString('en-US')}` : '—');

// Illustrative — the live leads/CRM pipeline lands in T1.
const SAMPLE_LEADS = [
  {
    name: 'Carlos M.',
    initials: 'CM',
    listing: 'Toyota Corolla 2019',
    time: '2h',
    tier: 'hot' as const,
  },
  { name: 'Ana R.', initials: 'AR', listing: 'Honda CR-V 2020', time: '5h', tier: 'med' as const },
  {
    name: 'Luis P.',
    initials: 'LP',
    listing: 'Toyota Corolla 2019',
    time: '1d',
    tier: 'med' as const,
  },
];

export const DealerDashboard: React.FC = () => {
  const authChrome = useAuthChrome();
  const navigate = useNavigate();
  const [listings, setListings] = useState<SellerListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let active = true;
    fetchMyListings()
      .then((rows) => active && setListings(rows))
      .catch(
        (err) =>
          active &&
          setError(err instanceof Error ? err.message : 'No se pudo cargar el inventario.'),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const active = listings.filter((l) => l.status === 'ACTIVE').length;

  return (
    <div className="cia-deal">
      <TopBar {...authChrome} nav={SELLER_NAV} />
      <ColorRail />

      <div className="cia-deal-head">
        <div className="cia-deal-eye">Panel del dealer</div>
        <h1 className="cia-deal-h1">Tu concesionario</h1>
      </div>

      <div className="cia-deal-kpis">
        <div className="cia-deal-kpi">
          <div className="cia-deal-kpi-row">
            <span className="cia-deal-kpi-label">Inventario</span>
            <span className="cia-deal-kpi-ic">
              <SuvIcon size={18} strokeWidth={1.7} />
            </span>
          </div>
          <span className="cia-deal-kpi-val">{listings.length}</span>
          <span className="cia-deal-kpi-foot cia-deal-kpi-up">{active} activos</span>
        </div>
        <div className="cia-deal-kpi">
          <div className="cia-deal-kpi-row">
            <span className="cia-deal-kpi-label">Activos</span>
            <span className="cia-deal-kpi-ic">
              <ShieldTrustIcon size={18} strokeWidth={1.8} />
            </span>
          </div>
          <span className="cia-deal-kpi-val">{active}</span>
          <span className="cia-deal-kpi-foot cia-deal-kpi-up">en el catálogo</span>
        </div>
        <div className="cia-deal-kpi">
          <div className="cia-deal-kpi-row">
            <span className="cia-deal-kpi-label">Leads · mes</span>
            <span className="cia-deal-kpi-ic">
              <CoinIcon size={18} strokeWidth={1.8} />
            </span>
          </div>
          <span className="cia-deal-kpi-val">—</span>
          <span className="cia-deal-kpi-foot cia-deal-kpi-sample">ejemplo · T1</span>
        </div>
        <div className="cia-deal-kpi">
          <div className="cia-deal-kpi-row">
            <span className="cia-deal-kpi-label">Conversión</span>
            <span className="cia-deal-kpi-ic">
              <ScaleIcon size={18} strokeWidth={1.8} />
            </span>
          </div>
          <span className="cia-deal-kpi-val">—</span>
          <span className="cia-deal-kpi-foot cia-deal-kpi-sample">ejemplo · T1</span>
        </div>
      </div>

      <div className="cia-deal-sec">
        <span className="cia-deal-sec-title">Top inventario</span>
        <span className="cia-deal-sec-count">{listings.length}</span>
      </div>
      {loading && <p className="cia-deal-state">Cargando…</p>}
      {!loading && error && (
        <p className="cia-deal-state" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && listings.length === 0 && (
        <p className="cia-deal-state">Sin inventario todavía.</p>
      )}
      {listings.length > 0 && (
        <div className="cia-deal-inv">
          {listings.map((l, i) => (
            <button
              key={l.id}
              className="cia-deal-inv-row"
              onClick={() => navigate(`/vehicles/${l.id}`)}
            >
              <span
                className="cia-deal-inv-thumb"
                style={{ backgroundImage: `url('${FALLBACK[i % FALLBACK.length]}')` }}
              />
              <span className="cia-deal-inv-info">
                <span className="cia-deal-inv-title">
                  {l.make} {l.model} {l.year}
                </span>
                <span className="cia-deal-inv-meta">
                  {l.status} · {l.mileage.toLocaleString('en-US')} km
                </span>
              </span>
              <span className="cia-deal-inv-price">{usd(l.listingPrice)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="cia-deal-sec">
        <span className="cia-deal-sec-title">Lead inbox</span>
      </div>
      <div className="cia-deal-preview">
        Vista previa · datos de ejemplo. La bandeja de leads y el CRM llegan en T1.
      </div>
      <div className="cia-deal-leads">
        {SAMPLE_LEADS.map((l) => (
          <div key={l.name} className="cia-deal-lead">
            <span className="cia-deal-lead-av">{l.initials}</span>
            <span>
              <span className="cia-deal-lead-r1">
                <span className="cia-deal-lead-name">{l.name}</span>
                <span className="cia-deal-lead-time">{l.time}</span>
              </span>
              <span className="cia-deal-lead-listing">{l.listing}</span>
              <span className={`cia-deal-lead-tier cia-deal-lead-tier--${l.tier}`}>
                {l.tier === 'hot' ? 'HOT' : 'MEDIO'}
              </span>
            </span>
          </div>
        ))}
      </div>

      <BottomNav />
    </div>
  );
};

export default DealerDashboard;
